import Pusher from 'pusher-js';
import * as PusherPushNotifications from '@pusher/push-notifications-web';

let pusherInstance = null;
let beamsClientInstance = null;

/**
 * Initialize Pusher Channels (WebSocket)
 * Pass the apiCall function from AuthContext for private channel auth
 */
export const initPusher = (apiCall) => {
    if (pusherInstance) return pusherInstance;

    // Enable logging in development
    if (process.env.NODE_ENV === 'development') {
        Pusher.logToConsole = true; // Shows connect/disconnect in console
    }

    pusherInstance = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER,
        forceTLS: true,
        channelAuthorization: {
            transport: 'ajax',
            customHandler: async (params, callback) => {
                try {
                    // Token is fetched dynamically every time auth is needed
                    const token =
                        localStorage.getItem('accessToken') ||
                        localStorage.getItem('token') ||
                        sessionStorage.getItem('accessToken') ||
                        sessionStorage.getItem('token');

                    const response = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/api/notifications/pusher/auth`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                socket_id: params.socketId,
                                channel_name: params.channelName,
                            }),
                        }
                    );

                    if (!response.ok) {
                        const err = new Error(`Auth failed: ${response.status}`);
                        console.error('❌ Pusher channel auth failed:', response.status);
                        return callback(err, null);
                    }

                    const data = await response.json();
                    callback(null, data);
                } catch (error) {
                    console.error('❌ Pusher auth error:', error.message);
                    callback(error, null);
                }
            },
        },
    });

    pusherInstance.connection.bind('connected', () => {
        console.log('✅ Pusher WebSocket: Connected', pusherInstance.connection.socket_id);
    });

    pusherInstance.connection.bind('disconnected', () => {
        console.warn('⚠️ Pusher WebSocket: Disconnected');
    });

    pusherInstance.connection.bind('error', (err) => {
        console.error('❌ Pusher WebSocket error:', err);
    });

    return pusherInstance;
};

/**
 * Subscribe to user's private channel
 * @param {string} userId - MongoDB _id of the logged-in user
 * @param {Function} onNotification - callback(eventName, data)
 */
export const subscribeToUserChannel = (userId, onNotification) => {
    if (!pusherInstance) return null;

    const channelName = `private-user-${userId}`;
    const channel = pusherInstance.subscribe(channelName);

    channel.bind('pusher:subscription_succeeded', () => {
        console.log(`✅ Subscribed to channel: ${channelName}`);
    });

    channel.bind('pusher:subscription_error', (err) => {
        console.error(`❌ Channel subscription error (${channelName}):`, err);
    });

    // Bind notification events
    [
        'leave-applied', 'leave-status-updated', 'payslip', 'asset-assigned', 'award-given',
        'compoff-applied', 'compoff-status-updated',
        'document-uploaded', 'document-approved', 'policy-added',
        'reimbursement-submitted', 'reimbursement-accepted', 'reimbursement-rejected',
        'project-allocation', 'project-reassignment', 'project-removed', 'project-created',
        'resignation-submitted', 'resignation-approved', 'resignation-rejected', 'resignation-withdrawn',
        'general'
    ].forEach(event => {
        channel.bind(event, (data) => {
            console.log(`📨 Notification [${event}]:`, data);
            onNotification(event, data);
        });
    });

    return channel;
};

/**
 * Initialize Pusher Beams (Browser Push Notifications)
 * @param {string} userId - MongoDB _id of the logged-in user
 * @param {Function} getToken - async function that returns beams token from backend
 */
export const initBeams = async (userId, getToken) => {
    try {
        if (beamsClientInstance) return beamsClientInstance;

        if (!('Notification' in window)) {
            console.warn('⚠️ Browser does not support notifications');
            return null;
        }
        if (!('serviceWorker' in navigator)) {
            console.warn('⚠️ Service workers not supported');
            return null;
        }

        const swRegistration = await navigator.serviceWorker.register('/push-notifications-sw.js');
        beamsClientInstance = new PusherPushNotifications.Client({
            instanceId: process.env.REACT_APP_PUSHER_BEAMS_INSTANCE_ID,
            serviceWorkerRegistration: swRegistration,
        });

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.warn('⚠️ Notification permission denied — Beams skipped');
            return null;
        }

        await beamsClientInstance.start();

        // Authenticate with backend token
        await beamsClientInstance.setUserId(`user-${userId}`, {
            fetchToken: getToken,
        });

        console.log(`✅ Pusher Beams initialized for user-${userId}`);
        return beamsClientInstance;
    } catch (error) {
        console.error('❌ Pusher Beams init error:', error.message);
    }
};

/**
 * Disconnect Pusher on logout
 */
export const disconnectPusher = () => {
    if (pusherInstance) {
        pusherInstance.disconnect();
        pusherInstance = null;
        console.log('🔌 Pusher WebSocket: Disconnected (logout)');
    }
    if (beamsClientInstance) {
        beamsClientInstance.stop().catch(() => { });
        beamsClientInstance = null;
    }
};